import { buildEpisodeLink } from '@/pages/PatientDetailsPage/PatientSchedulingTab/PatientEpisodeView/routes';
import { OrderType, Ordering, SyncState, VisitRequestWorkflowStep } from '@/types/graphql';
import { Utils } from '@/types/utils';
import { VisitOutcome, VisitState } from 'gql-types';
import { merge } from 'lodash';
import moment from 'moment';

import { GraphInputHelpers } from '../../../fetching/graphql/GraphInputHelpers';
import { DashboardContainer, DashboardContainerDefinition, RootEntity } from './types';

export const DEFAULT_CONTAINER_FILTER_WINDOW_DAYS = 31;

export enum ContainerDefinitionType {
    CLINICIAN_CALL_OUTS = 'clinicianCallOuts',
    CLINICIAN_PATIENT_PAYER_MISMATCH = 'clinicianPatientPayerMismatch',
    CLINICIAN_UTILIZATION = 'clinicianUtilization',
    DISCHARGE = 'discharge',
    EVALS = 'evals',
    FREQUENCY_ORDERS = 'frequencyOrders',
    MISSED = 'missed',
    OBSERVATIONS = 'observations',
    OVERDUE_EPISODE_VISIT = 'overdue',
    PATIENT_SYNC_ISSUES = 'patientSyncIssues',
    PENDING_ADMISSION_PATIENTS = 'pendingAdmissionPatients',
    PENDING_END_OF_CARE = 'pendingEndOfCare',
    REASSIGN = 'reassign',
    RESCHEDULE = 'reschedule',
    SOCS = 'socs',
    TRANSFERS = 'transfers',
    UNASSIGNED = 'unassigned',
    UNASSIGNED_EPISODE_VISIT = 'unassignedEpisodeVisit',
    UNSYNCED_PATIENTS = 'unsyncedPatients',
    VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES = 'visitClinicianAppointmentSyncIssues',
    VISIT_SYNC_ISSUES = 'visitSyncIssues',
}

export type ContainerDefinitionKey = ContainerDefinitionType | string;

export const defaultCliniciansRowToLink = (row: { id: string }): string => `/clinicians/${row.id}`;
export const defaultEpisodeOfCareRowToLink = (row: {
    id: string;
    patient: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.id });
export const defaultOrdersRowToLink = (row: { id: string }): string => `/orders/${row.id}`;
export const defaultPatientsRowToLink = (row: { id: string }): string => `/patients/${row.id}`;
export const defaultVisitsRowToLink = (row: { id: string }): string => `/visits/${row.id}`;
export const defaultVisitRequestsRowToLink = (row: {
    id: string;
    visit: { id: string } | null;
}): string => (row.visit ? defaultVisitsRowToLink(row.visit) : `/visit-requests/${row.id}`);
export const defaultEpisodeVisitsRowToLink = (row: {
    patient: { id: string };
    episode: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id });
export const defaultEpisodeVisitRequestsRowToLink = (row: {
    patient: { id: string };
    episode: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id });

const CONTAINER_DEFINITIONS: Record<
    ContainerDefinitionType,
    DashboardContainerDefinition<RootEntity>
> = {
    [ContainerDefinitionType.CLINICIAN_CALL_OUTS]: {
        key: ContainerDefinitionType.CLINICIAN_CALL_OUTS,
        label: 'Clinician Call-Outs',
        tableConfig: {
            entity: RootEntity.CLINICIAN,
            filters: {
                shiftRecurrences: { isCanceled: { exact: true } },
            },
            rowToLink: defaultCliniciansRowToLink,
        },
    },
    [ContainerDefinitionType.CLINICIAN_PATIENT_PAYER_MISMATCH]: {
        key: ContainerDefinitionType.CLINICIAN_PATIENT_PAYER_MISMATCH,
        label: 'Payer Mismatch',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                clinicianAppointments: {
                    clinician: { id: { isNull: false } },
                    arePatientClinicianPayersCompatible: { exact: false },
                },
                outcome: { exact: VisitOutcome.NONE },
            },
            emptyTableText: 'No payer mismatches at this time.',
            rowToLink: defaultVisitsRowToLink,
        },
        hoverText: 'Visits with clinician and patient payer mismatches.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.SOCS]: {
        key: ContainerDefinitionType.SOCS,
        label: 'SOCs',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { services: { name: { iContains: 'start of care' } } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.EVALS]: {
        key: ContainerDefinitionType.EVALS,
        label: 'Evals',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { services: { name: { iContains: 'eval' } } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.FREQUENCY_ORDERS]: {
        key: ContainerDefinitionType.FREQUENCY_ORDERS,
        label: 'Frequency Orders',
        tableConfig: {
            entity: RootEntity.ORDER,
            filters: {
                orderType: { exact: OrderType.FREQUENCY },
                activeVisitRequests: {
                    workflowStep: {
                        inList: [
                            VisitRequestWorkflowStep.NEEDS_SERVICES,
                            VisitRequestWorkflowStep.NEEDS_CLINICIAN,
                        ],
                    },
                },
            },
            rowToLink: defaultOrdersRowToLink,
        },
    },
    [ContainerDefinitionType.OVERDUE_EPISODE_VISIT]: {
        key: ContainerDefinitionType.OVERDUE_EPISODE_VISIT,
        label: 'Overdue',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                state: { inList: [VisitState.SCHEDULED, VisitState.CONFIRMED] },
                startDatetime: { lt: GraphInputHelpers.datetime(moment().subtract(15, 'minutes')) },
            },
            rowToLink: defaultVisitsRowToLink,
            ordering: {
                startDatetime: Ordering.ASC,
            },
            emptyTableText: 'No visits overdue by more than 15 mins.',
        },
        hoverText: 'Upcoming visits more than 15 mins. overdue.',
        extraProps: {
            showClinicianColumn: true,
        },
    },
    [ContainerDefinitionType.PENDING_ADMISSION_PATIENTS]: {
        key: ContainerDefinitionType.PENDING_ADMISSION_PATIENTS,
        label: 'Pending Admission Patients',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { episodes: { id: { isNull: true } } },
            ordering: { createdAt: Ordering.DESC },
            emptyTableText: 'No pending patients at this time.',
            rowToLink: defaultPatientsRowToLink,
        },
        hoverText: 'New referrals.',
    },
    [ContainerDefinitionType.PENDING_END_OF_CARE]: {
        key: ContainerDefinitionType.PENDING_END_OF_CARE,
        label: 'Pending End of Care',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: {
                episodes: {
                    endDate: {
                        range: {
                            start: GraphInputHelpers.date(new Date()),
                            end: GraphInputHelpers.date(
                                new Date(new Date().setDate(new Date().getDate() + 30)),
                            ),
                        },
                    },
                },
            },
            rowToLink: defaultPatientsRowToLink,
        },
    },
    [ContainerDefinitionType.TRANSFERS]: {
        key: ContainerDefinitionType.TRANSFERS,
        label: 'Transfers',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { visits: { services: { name: { iContains: 'transfer' } } } },
            rowToLink: defaultPatientsRowToLink,
        },
    },
    [ContainerDefinitionType.OBSERVATIONS]: {
        key: ContainerDefinitionType.OBSERVATIONS,
        label: 'Observations',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { notes: { iContains: 'obs' } },
            rowToLink: defaultPatientsRowToLink,
        },
    },
    [ContainerDefinitionType.RESCHEDULE]: {
        key: ContainerDefinitionType.RESCHEDULE,
        label: 'Reschedule',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                state: { exact: VisitState.UNSCHEDULED },
                OR: { requiresReschedule: { exact: true } },
            },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.MISSED]: {
        key: ContainerDefinitionType.MISSED,
        label: 'Missed',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { outcome: { exact: VisitOutcome.MISSED } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.PATIENT_SYNC_ISSUES]: {
        key: ContainerDefinitionType.PATIENT_SYNC_ISSUES,
        label: 'Patient Sync Issues',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { syncStatus: { state: { exact: SyncState.FAILING } } },
            emptyTableText: 'No sync issues at this time',
            rowToLink: defaultPatientsRowToLink,
        },
        hoverText: 'Patients unable to be synced to EMR.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.REASSIGN]: {
        key: ContainerDefinitionType.REASSIGN,
        label: 'Reassign',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { needsClinicians: { exact: true } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.UNASSIGNED]: {
        key: ContainerDefinitionType.UNASSIGNED,
        label: 'Unassigned',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { needsClinicians: { exact: true }, outcome: { exact: VisitOutcome.NONE } },
            emptyTableText: 'No unassigned visits at this time',
            rowToLink: defaultVisitsRowToLink,
            ordering: {
                startDatetime: Ordering.ASC,
            },
        },
        hoverText: 'Visits in need of a clinician assignment.',
    },
    [ContainerDefinitionType.UNASSIGNED_EPISODE_VISIT]: {
        key: ContainerDefinitionType.UNASSIGNED,
        label: 'Unassigned',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { needsClinicians: { exact: true }, outcome: { exact: VisitOutcome.NONE } },
            emptyTableText: 'No unassigned visits at this time',
            rowToLink: defaultEpisodeVisitsRowToLink,
            ordering: {
                startDatetime: Ordering.ASC,
            },
        },
        hoverText: 'Visits in need of a clinician assignment.',
    },
    [ContainerDefinitionType.UNSYNCED_PATIENTS]: {
        key: ContainerDefinitionType.UNSYNCED_PATIENTS,
        label: 'Patient Sync Issues',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { externalIds: { value: { isNull: true } } },
            emptyTableText: 'No sync issues at this time',
            rowToLink: defaultPatientsRowToLink,
        },
        hoverText: 'Patients unable to be synced to EMR.',
    },
    [ContainerDefinitionType.DISCHARGE]: {
        key: ContainerDefinitionType.DISCHARGE,
        label: 'Discharge',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { services: { name: { iContains: 'discharge' } } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.CLINICIAN_UTILIZATION]: {
        key: ContainerDefinitionType.CLINICIAN_UTILIZATION,
        label: 'Clinician Utilization',
        tableConfig: {
            entity: RootEntity.CLINICIAN,
            filters: {},
            rowToLink: defaultCliniciansRowToLink,
        },
    },
    [ContainerDefinitionType.VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES]: {
        key: ContainerDefinitionType.VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES,
        label: 'Appointment Sync Issues',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                clinicianAppointments: { syncStatus: { state: { exact: SyncState.FAILING } } },
                outcome: { exact: VisitOutcome.NONE },
            },
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No sync issues at this time.',
        },
        hoverText: 'Visits unable to be synced to EMR.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.VISIT_SYNC_ISSUES]: {
        key: ContainerDefinitionType.VISIT_SYNC_ISSUES,
        label: 'Visit Sync Issues',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                syncStatus: { state: { exact: SyncState.FAILING } },
                outcome: { exact: VisitOutcome.NONE },
            },
            emptyTableText: 'No sync issues at this time.',
            rowToLink: defaultVisitsRowToLink,
        },
        hoverText: 'Visits unable to be synced to EMR.',
        heavyQuery: true,
    },
};

export function makeContainerDefinition({
    type,
    defaultEnabled,
    overrides = {},
}: {
    type: ContainerDefinitionType;
    defaultEnabled: boolean;
    overrides?: Utils.RecursivePartial<DashboardContainer<RootEntity>>;
}): DashboardContainer<RootEntity> {
    const baseDefinition = CONTAINER_DEFINITIONS[type];
    return merge({}, baseDefinition, overrides, { defaultEnabled });
}
